<script setup>
import { ref, watch, toRef } from 'vue'
import { getI18n } from '@/plugins/i18n'

import 'devextreme-vue/text-area'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'

import { useColumns } from '@/composables/sale/clipboard-row-grid'
import { useDocumentRow } from '@/composables/document-row'

import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useSaleStore } from '@/stores/sale.store'
import { usePortalStore } from '@/stores/portal.store'

import { useProductService } from '@/services/product.service'
import { useEntityLock } from '@/services/entity-lock.service'

import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import ProductDropDownBox from '@/components/shared/ProductDropDownBox.vue'

import ChipStatus from '@/components/shared/ChipStatus.vue'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import DocumentRowTypeDropDownBox from '@/components/shared/DocumentRowTypeDropDownBox.vue'
import DocumentRowTypeToolbar from '@/components/shared/DocumentRowTypeToolbar.vue'
import SupersessionProductDropDownBox from '@/components/shared/SupersessionProductDropDownBox.vue'
import TimeEquivalentTextBox from '@/components/shared/TimeEquivalentTextBox.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'
import ImportRowIcon from '@/components/shared/ImportRowIcon.vue'

const props = defineProps({
  gridId: {
    type: String,
    default: null,
  },
  group: {
    type: Number,
    default: null,
  },
  selectMode: {
    type: Boolean,
    default: false,
  },
  selectedRows: {
    type: Array,
    default: () => [],
  },
  dataSource: {
    type: [Array, Object],
    default: null,
  },
})

const gridId = props.gridId || `clipboard-row-grid`
const i18n = getI18n()
const { route, router } = dataGrid.useGridRouter()

const webSiteStore = useWebSiteStore()
const registryStore = useRegistryStore()
const saleStore = useSaleStore()
const portalStore = usePortalStore()

const productService = useProductService()

const isToolbarDisabled = ref(true)
const supersessionId = ref(null)
const productUnitOfMeasure = ref(null)
const editors = ref({})
const keepDescription = ref(true)

const { gridRef, refreshGridAsync, focusGrid, setCellValueByField } = dataGrid.useGridRef()
const { documentRowType, onDocumentRowUpdated, onDocumentRowInitNewRow, onDocumentRowEditorPreparing, onDocumentRowEditorPreparingSetIsReadOnly, onDocumentRowTypeChanged, focusQuantityEditor } = useDocumentRow(gridRef)

watch(() => webSiteStore.userDataFilter, () => gridRef.value.instance.refresh())

defineExpose({ focusedRow: dataGrid.focusedRow, gridRef })
const emit = defineEmits(['update:selectedRows'])
const selectedRows = toRef(props, 'selectedRows')
watch(selectedRows, (newValue) => {
  if (gridRef.value) {
    const newKeys = newValue?.map(i => i.id)
    if (!newKeys?.length) {
      gridRef.value.instance.deselectAll()
    } else {
      gridRef.value.instance.selectRows(newKeys, false)
    }
  }
}, { immediate: true })

const entityLock = !props.selectMode ? useEntityLock({
  baseLockUrl: saleStore.api.getAbsoluteUrl() + saleStore.api.clipboardRowsUrl,
  modelName: 'ClipboardRow',
  gridRef,
}) : null

const rowButtons = entityLock?.getEntityLockButtons()

const formInstance = ref(null)
const supplierBrand = ref(null)

const dataSource = dataGrid.useODataSource(
  saleStore.api.getAbsoluteUrl() + saleStore.api.clipboardRowsUrl,
  { filter: props.group ? ['group', '=', props.group] : undefined }
)

const { defaultDialogTitle } = dataGrid.useDefaultDialogTitle()

const getEditColumns = (documentRowType) => {
  let mainInfoDataFields = []
  const isLaborRow = documentRowType === webSiteStore.documentRowTypesEnum.LABOR
  const hideTabs = [webSiteStore.documentRowTypesEnum.TEXT, webSiteStore.documentRowTypesEnum.FREE].includes(documentRowType)

  if (documentRowType === webSiteStore.documentRowTypesEnum.TEXT) {
    mainInfoDataFields = [
      { dataField: 'document_row_type', editorOptions: { readOnly: true }, colSpan: 6 },
      { itemType: 'empty', colSpan: 6 },
      { dataField: 'description', colSpan: 12 },
      { dataField: 'notes', colSpan: 12, label: { text: i18n.t('common.internalNote') } },
    ]
  } else if (documentRowType === webSiteStore.documentRowTypesEnum.FREE) {
    mainInfoDataFields = [
      { dataField: 'document_row_type', editorOptions: { readOnly: true }, colSpan: 6 },
      { itemType: 'empty', colSpan: 6 },
      { dataField: 'description', colSpan: 12 },
      { dataField: 'notes', colSpan: 12, label: { text: i18n.t('common.internalNote') } },
      { dataField: 'quantity', editorOptions: dataGrid.useQuantityMinValueOptions(documentRowType), colSpan: 6 },
      { dataField: 'unit_of_measure_ref', colSpan: 6 },
    ]
  } else {
    mainInfoDataFields = [
      { dataField: 'document_row_type', editorOptions: { readOnly: true }, colSpan: 6 },
      { itemType: 'empty', colSpan: 6 },
      { dataField: 'supplier_brand_ref', editorOptions: { showClearButton: true }, colSpan: 4 },
      { dataField: 'product_ref', colSpan: 8 },
      { dataField: 'description', colSpan: 12 },
      { dataField: 'notes', colSpan: 12, label: { text: i18n.t('common.internalNote') } },
      { dataField: 'quantity', editorOptions: dataGrid.useQuantityMinValueOptions(documentRowType), colSpan: documentRowType === webSiteStore.documentRowTypesEnum.LABOR ? 3 : 6 },
      { dataField: 'unit_of_measure_ref', colSpan: documentRowType === webSiteStore.documentRowTypesEnum.LABOR ? 3 : 6 },
    ]
    if (isLaborRow) {
      mainInfoDataFields.push(
        { dataField: 'timeEquivalent', editorOptions: { readOnly: true }, visible: true, colSpan: 6 }
      )
    }
  }

  return [
      {
        itemType: 'group',
        colSpan: 2,
        items: [
          {
            itemType: 'group',
            caption: i18n.t('common.mainInfo'),
            name: 'mdi-view-list',
            captionTemplate: 'row-type-caption-template',
            colCount: 12,
            items: mainInfoDataFields
          },
          documentRowType !== webSiteStore.documentRowTypesEnum.TEXT ? {
            itemType: 'group',
            caption: i18n.t('common.additionalInfo'),
            name: 'mdi-currency-usd-circle',
            captionTemplate: 'generic-caption-template',
            cssClass: 'my-form-wrapper',
            colCount: 4,
            items: [
              { dataField: 'product_cost', editorOptions: { readOnly: true, ...dataGrid.useCurrencyOptions() }, label: { text: i18n.t('common.cost') } },
              { dataField: 'cost_valorization' },
              { dataField: 'discount_category_ref', colSpan: 2, editorOptions: { readOnly: true } },
              { dataField: 'product_price', editorOptions: dataGrid.useCurrencyOptions({readOnly: true}) },
              { dataField: 'charge_percentage', editorType: 'dxNumberBox', editorOptions: dataGrid.usePercentageOptions()},
              { dataField: 'price', label: { text: i18n.t('common.unitPriceNoVat') }, editorOptions: { ...dataGrid.useCurrencyOptions({precision: registryStore.companyData.default_display_price_precision}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
              { dataField: 'price_vat', label: { text: i18n.t('common.unitPriceVat') }, editorOptions: { ...dataGrid.useCurrencyOptions({precision: registryStore.companyData.default_display_price_precision}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
              { dataField: 'discount', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions(documentRowType) } },
              { dataField: 'discount_2', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions(documentRowType) } },
              { dataField: 'discount_3', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions(documentRowType) } },
              { dataField: 'total_discount', editorOptions: {...dataGrid.usePercentageOptions(), readOnly: true }, label: { text: i18n.t('common.totalDiscount') } },
              { dataField: 'vat_type_ref', editorOptions: { showClearButton: true }, colSpan: 2 },
              { dataField: 'total_no_vat', editorOptions: { ...dataGrid.useCurrencyOptions({readOnly: true}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
              { dataField: 'total_vat', editorOptions: { ...dataGrid.useCurrencyOptions({readOnly: true}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
            ]
          } : { itemType: 'empty' }
        ]
      },
      {
        itemType: 'tabbed',
        colSpan: 3,
        name: 'tabbed',
        tabs: hideTabs ? [] : [
          ...(isLaborRow ? [
            {
              title: i18n.t('common.times'),
              items: [
                {
                  itemType: 'group',
                  caption: i18n.t('common.times'),
                  colCount: 2,
                  items: [
                    { dataField: 'actual_duration', label: { text: i18n.t('common.timeClock') }, editorOptions: { min: 0 } },
                    { dataField: 'suggestedTime' },
                    { dataField: 'operationCode' },
                    { dataField: 'recognizedTime' },
                  ]
                },
              ]
            },
            {
              title: i18n.t('common.statistics'),
              items: [{ itemType: 'group', caption: i18n.t('common.statistics') }]
            },
          ] : [
            {
              title: i18n.t('common.stock'),
              items: [
                {
                  itemType: 'group',
                  caption: i18n.t('common.stock'),
                  colCount: 2,
                  items: [{ dataField: 'deposit' }, { dataField: 'garage' }]
                },
              ]
            },
            {
              title: i18n.t('common.ties'),
              items: [
                {
                  itemType: 'group',
                  caption: i18n.t('common.ties'),
                  items: [{ template: 'supersessionProductEditor', colSpan: 5 }]
                },
              ]
            },
            {
              title: i18n.t('common.campaigns'),
              items: [{ itemType: 'group', caption: i18n.t('common.campaigns') }]
            },
            {
              title: i18n.t('common.statistics'),
              items: [{ itemType: 'group', caption: i18n.t('common.statistics') }]
            },
            {
              title: i18n.t('common.operatorsStock'),
              items: [{ itemType: 'group', caption: i18n.t('common.operatorsStock') }]
            },
          ]),
        ]
      },
  ]
}

const columns = useColumns(supplierBrand, keepDescription, documentRowType, supersessionId, productUnitOfMeasure, props.selectMode)

const editing = dataGrid.useDefaultEditing(i => defaultDialogTitle(i, i18n.t('common.clipboard')), {
  colCount: 5,
  items: getEditColumns(documentRowType.value),
  onInitialized: (e) => { formInstance.value = e.component },
}, {
  allowAdding: !props.selectMode,
  allowUpdating: !props.selectMode,
  allowDeleting: !props.selectMode,
  isSaveAndNew: true,
  onInitializedCustom: (e) => entityLock.readOnlyToolbarItems(e),
  entityLock: entityLock
})

const onInitNewRow = (e) => {
  e.data.quantity = 1
  e.data.supplier_brand_ref = registryStore.companyData.default_supplier_brand_ref

  supersessionId.value = productUnitOfMeasure.value = null
  supplierBrand.value = registryStore.supplierBrands.find(i => i.id === registryStore.companyData.default_supplier_brand_ref)
  productService.updateTotalDiscountValue(e.data, e.data)
  onDocumentRowInitNewRow(e, supplierBrand, 0, isToolbarDisabled)
  onDocumentRowTypeChanged(documentRowType.value, getEditColumns)
}

const onEditorPreparing = (e) => {
  const includesVat = e.row?.data?.source_includes_vat || false
  onDocumentRowEditorPreparing(e, isToolbarDisabled, supplierBrand, includesVat)

  if (e.parentType === 'dataRow') {
    e.editorOptions.onInitialized = (arg) => { editors.value[e.dataField] = arg.component }
  }

  if (e.parentType === 'dataRow' && e.dataField === 'product_ref') {
    e.editorOptions.disabled = !!e.row.data?.id || (typeof e.row.data?.supplier_brand_ref !== 'number')
  }
  if (e.parentType === 'dataRow' && e.dataField === 'product_price') {
    e.editorOptions.readOnly = true
  }

  onDocumentRowEditorPreparingSetIsReadOnly(e, props.readOnly, editors)
}

const onDocumentRowTypeSelect = (rowType) => {
  documentRowType.value = rowType
  supplierBrand.value = null
  setCellValueByField('document_row_type', rowType)
  onDocumentRowTypeChanged(rowType, getEditColumns)
}

const onEditingStart = (e) => {
  supersessionId.value = e.data?.product_ref || null
  productUnitOfMeasure.value = e.data?.unit_of_measure_ref || null

  documentRowType.value = e.data?.document_row_type ?? webSiteStore.getDefaultDocumentRowType()

  productService.updatePriceVatValue(e.data, e.data)
  productService.updateTotalDiscountValue(e.data, e.data)
  onDocumentRowTypeChanged(documentRowType.value, getEditColumns)
}

const onToolbarPreparing = (e) => {
  e.toolbarOptions.visible = !props.selectMode
  e.toolbarOptions.items.unshift(
    { location: 'after', template: 'dropdownToolbarItem' },
  )
  dataGrid.useDefaultToolbar(e, {
    addButtonOptions: { hint: `${i18n.t('common.add')} ${i18n.t('common.document')}` },
  })
  e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
}

const onSelectionChanged = (e) => emit('update:selectedRows', e.selectedRowsData.length ? e.selectedRowsData : null)

const onSupersessionSelected = () => {
  const tabPanel = formInstance.value?.getEditor('tabbed')
  const tiesTabIndex = tabPanel?.option('items')?.findIndex(tab => tab.title === i18n.t('common.ties'))
  if (tiesTabIndex >= 0) {
    tabPanel.option('selectedIndex', tiesTabIndex)
  }
}

const onProductValueChanged = (e) => {
  if (e.value) { focusQuantityEditor(editors) }
}

const onNewProductSelected = (e) => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const rowIndex = editingRow.rowIndex
  grid.cellValue(rowIndex, 'product_ref', e)
  supersessionId.value = e?.id || null
}

const onSaving = (e) => {
  if (e.changes?.[0]?.type === 'insert') {
    e.changes[0].data.group = props.group
  }
}

let gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataSource,
  columns: columns,
  height: '100%',
})

if (props.selectMode) {
  gridOptions.selection = dataGrid.useDefaultGridSelection()
  gridOptions.columnChooser = { enabled: false }
  gridOptions.paging = { enabled: true, pageSize: 10 }
  gridOptions.focusedRowEnabled = false
  gridOptions.onSelectionChanged = onSelectionChanged
  gridOptions.columns = [...columns.filter(i => i.dataField !== 'unit_of_measure' && i.dataField !== 'discount_category')]
}

gridOptions = {...gridOptions, ...{
  editing: editing,
  splittedGridDetail: true,
  onInitNewRow: onInitNewRow,
  onEditorPreparing: onEditorPreparing,
  onEditingStart: onEditingStart,
  onToolbarPreparing: onToolbarPreparing,
  onSelectionChanged: onSelectionChanged,
  onRowUpdated: onDocumentRowUpdated,
  onRowInserted: onDocumentRowUpdated,
  onSaving: onSaving,
  routerFilter: {
    fields: [{series: 'series'}, {number: 'number_mnemonic'}],
    getRoute: () => route,
    router: router
  }
}}

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #dropdownToolbarItem>
        <import-row-icon
          :document-type="portalStore.documentTypesEnum.CLIPBOARD"
          :document-id="props.group"
          :displayed-sources="[webSiteStore.importRowTypesEnum.CATALOG, webSiteStore.importRowTypesEnum.LRW]"
          @updated="refreshGridAsync().then(focusGrid)"
        />
      </template>
      <template #gridLayoutConfigTemplate>
        <grid-layout-config-template
          :grid-ext-ref="gridRef"
          :grid-ext-id="gridId"
        />
      </template>
      <template #productEditor="{ data: cellInfo }">
        <product-drop-down-box
          :brand="supplierBrand"
          :document-row-type="documentRowType"
          :data="cellInfo"
          :disabled="entityLock?.isReadOnly.value"
          :options="{ clipboard: true }"
          @supersession-selected="onSupersessionSelected"
          @value-changed="onProductValueChanged"
        />
      </template>
      <template #supersessionProductEditor>
        <supersession-product-drop-down-box
          :data="{ supersession_id: supersessionId }"
          @new-product-selected="onNewProductSelected"
        />
      </template>
      <template #row-type-caption-template="{ data: cellInfo }">
        <document-row-type-toolbar
          :data="cellInfo"
          :disabled="props.readOnly || isToolbarDisabled"
          :document-row-type="documentRowType"
          @value-changed="(e) => onDocumentRowTypeSelect(e.value)"
        />
      </template>
      <template #generic-caption-template="{ data: cellInfo }">
        <div
          class="dx-form-group-caption-wrapper"
          style="min-height: 1.5rem;"
        >
          <q-icon
            :name="cellInfo.name"
            class="dx-form-group-caption-icon"
          />
          <span class="dx-form-group-caption-text">
            {{ cellInfo.caption }}
          </span>
        </div>
      </template>
      <template
        v-if="!props.selectMode"
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'ClipboardRow'"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="entityLock"
          :is-locked-available="true"
          :buttons="rowButtons"
        />
      </template>
      <template
        v-if="!props.selectMode"
        #customToolbarAddButtonTemplate
      >
        <custom-toolbar-add-button-template
          :grid="gridRef"
          :entity-lock="entityLock"
        />
      </template>
      <template #depositEditor="{ data: cellInfo }">
        <deposit-drop-down-box
          :data="cellInfo"
          :disabled="props.readOnly || documentRowType === webSiteStore.documentRowTypesEnum.TEXT"
        />
      </template>
      <template #documentRowType="{ data: cellInfo }">
        <document-row-type-drop-down-box
          :data="cellInfo"
          :value="cellInfo?.data?.document_row_type"
          :disabled="true"
        />
      </template>
      <template #timeEquivalentEditor>
        <time-equivalent-text-box
          :selected-brand-id="editors['supplier_brand_ref'].option('value')"
          :selected-quantity="editors['quantity'].option('value')"
          :selected-uom-id="productUnitOfMeasure"
        />
      </template>
      <template #documentRowTypeCellTemplate="{ data: templateOptions }">
        <chip-status :data="webSiteStore.getLocalizedDocumentRowTypes().find(x => x.id === templateOptions.data.document_row_type)" />
      </template>
    </dx-data-grid>
  </div>
</template>
<style>
</style>
